import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Sparkles, ShieldCheck, MapPin, Clock, ChevronDown, ChevronUp, Loader2, Info } from 'lucide-react';
import WorkerAvatar from '../shared/WorkerAvatar';

// iter98 (Feb 2026) — "Best match for this shift" recommendation card.
// Consumes GET /api/shifts/{id}/ranked-applicants which returns a
// deterministic weighted ranking with per-factor breakdown. This
// component only ever shows the TOP eligible applicant + a disclosure
// panel explaining the ranking. The manager still has to click "Choose"
// — nothing is auto-assigned. "View all applicants" scrolls back to the
// existing applicant list (rendered by the parent page).
//
// Safeguards:
//   • Only job-relevant factors are shown. No protected characteristics.
//   • If NO applicants are eligible (all failed hard requirements), the
//     card displays a neutral "Not enough eligible applicants yet" state
//     so managers know why nobody was recommended.
//   • If NO shift requirements were set, the card is still useful — it
//     just ranks based on general profile signals.

export default function BestMatchCard({ shiftId, onChoose }) {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showReasons, setShowReasons] = useState(false);
  const [choosing, setChoosing] = useState(false);

  const load = useCallback(async () => {
    if (!shiftId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/shifts/${shiftId}/ranked-applicants`);
      setData(data);
    } catch { setData(null); }
    finally { setLoading(false); }
  }, [api, shiftId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="border-0 shadow-sm" data-testid="best-match-loading">
        <CardContent className="p-5 flex items-center gap-3 text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Ranking applicants…
        </CardContent>
      </Card>
    );
  }

  const ranked = data?.ranked || [];
  const eligible = ranked.filter(r => r.eligible);
  const top = eligible[0];

  // No applicants at all — nothing to recommend.
  if (ranked.length === 0) return null;

  // Everyone hard-filtered out (Smart ID required + none verified, etc).
  if (!top) {
    return (
      <Card className="border-2 border-amber-200 bg-amber-50" data-testid="best-match-none-eligible">
        <CardContent className="p-5">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-amber-900">No fully-eligible applicants yet</p>
              <p className="text-sm text-amber-800 mt-0.5">
                Every applicant is missing at least one of the shift&apos;s hard requirements ({(ranked[0]?.hardBlockReasons || []).join(', ') || 'Smart ID / language'}).
                You can still choose any applicant below.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const yearsLabelMap = {
    less_than_1: 'Less than 1 year experience',
    '1_to_2': '1–2 years experience',
    '3_to_5': '3–5 years experience',
    '5_to_10': '5–10 years experience',
    '10_plus': '10+ years experience',
  };
  const yearsLabel = yearsLabelMap[top.yearsExperience] || null;
  const smartIdOk = top.smartIdVerified || top.smartIdManagerConfirmed || top.smartIdSelfDeclared;

  return (
    <Card className="border-0 shadow-md ring-1 ring-emerald-200 bg-emerald-50/40" data-testid="best-match-card">
      <CardContent className="p-5 sm:p-6">
        <div className="flex items-center gap-2 mb-3">
          <div className="h-8 w-8 rounded-full bg-emerald-600 flex items-center justify-center">
            <Sparkles className="h-4 w-4 text-white" />
          </div>
          <p className="text-sm font-semibold text-emerald-800 tracking-wide uppercase" data-testid="best-match-heading">
            Best match for this shift
          </p>
        </div>

        <div className="flex items-start gap-4">
          <WorkerAvatar name={top.workerName} profilePhoto={top.workerPhoto} size="lg" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2 flex-wrap">
              <h3 className="text-lg font-bold text-slate-900 truncate" data-testid="best-match-name">
                {top.workerName}
              </h3>
              <span className="text-base font-bold text-emerald-700" data-testid="best-match-score">
                {top.score}% match
              </span>
            </div>
            <ul className="mt-1 space-y-0.5 text-sm text-slate-700" data-testid="best-match-highlights">
              {top.currentPostOfficeRole && (<li>{top.currentPostOfficeRole}</li>)}
              {yearsLabel && (<li>{yearsLabel}</li>)}
              {smartIdOk && (<li className="flex items-center gap-1"><ShieldCheck className="h-3.5 w-3.5 text-emerald-700" /> Smart ID verified</li>)}
              {typeof top.reliabilityScore === 'number' && (<li>{top.reliabilityScore.toFixed(1)} reliability</li>)}
            </ul>
          </div>
        </div>

        <div className="mt-4 flex flex-col sm:flex-row gap-2">
          <Button
            onClick={() => { setChoosing(true); try { onChoose?.(top); } finally { setChoosing(false); } }}
            disabled={choosing}
            className="flex-1 h-11 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-70"
            data-testid="best-match-choose-btn"
          >
            {choosing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Choose ${(top.workerName || '').split(' ')[0]}`}
          </Button>
          <Button
            onClick={() => setShowReasons(v => !v)}
            variant="outline"
            className="h-11 rounded-full font-medium"
            data-testid="best-match-why-btn"
          >
            {showReasons ? (<><ChevronUp className="h-4 w-4 mr-1.5" /> Hide reasons</>) : (<><ChevronDown className="h-4 w-4 mr-1.5" /> Why this worker?</>)}
          </Button>
        </div>

        {showReasons && (
          <div className="mt-4 rounded-xl bg-white border border-slate-200 p-4 space-y-3" data-testid="best-match-reasons">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Ranking factors</p>
            <ul className="space-y-1.5">
              {top.factors.map(f => (
                <li key={f.key} className="flex items-start gap-2 text-sm text-slate-800" data-testid={`best-match-factor-${f.key}`}>
                  <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-emerald-500 shrink-0" />
                  <span>
                    <span className="font-semibold">{f.label}</span>
                    {f.detail ? <span className="text-slate-600"> — {f.detail}</span> : null}
                    {f.points > 0 ? <span className="text-slate-400 text-xs ml-1">(+{f.points})</span> : null}
                  </span>
                </li>
              ))}
            </ul>
            {top.gaps && top.gaps.length > 0 && (
              <>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-3">Also noted</p>
                <ul className="space-y-1.5">
                  {top.gaps.map(g => (
                    <li key={g.key} className="flex items-start gap-2 text-sm text-slate-600" data-testid={`best-match-gap-${g.key}`}>
                      <span className="mt-0.5 h-1.5 w-1.5 rounded-full bg-slate-300 shrink-0" />
                      <span>{g.label}{g.detail ? <span> — {g.detail}</span> : null}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
            <p className="text-xs text-slate-500 mt-2 flex items-center gap-1.5">
              <Info className="h-3.5 w-3.5" />
              Ranking uses only job-relevant signals. Never age, gender, race, nationality, religion or other protected characteristics.
            </p>
          </div>
        )}

        <p className="text-xs text-slate-500 mt-3" data-testid="best-match-scroll-hint">
          Prefer someone else? Choose any applicant from the list below.
        </p>
      </CardContent>
    </Card>
  );
}
